import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import 'antd/dist/antd.variable.min.css';

import Navbar from './components/Navbar';
import { useTranslate } from './hooks/translate';
import { ThemeProviderWithPersistence } from './components/ThemeProviderWithPersistence';
import AuthProvider, { useUser } from './context/authenticatedUser';
import { useIsCustomerManager } from './hooks/customerManagerToken';
import styles from './pages/app.module.css';
import Home from './pages';
import AdminsIndex from './pages/admins';
import AdminsCreate from './pages/admins/create';
import AdminsDelete from './pages/admins/delete';
import AdminsSystemAdminToken from './pages/admins/system-admin-token';
import AdminsCustomerManagerToken from './pages/admins/customer-manager-token';
import CustomersIndex from './pages/customers';
import CustomersCreate from './pages/customers/create';
import CustomersCreateInProgress from './pages/customers/create/in-progress';
import CustomersCreateInProgressById from './pages/customers/create/in-progress/[progressId]';
import CustomersById from './pages/customers/[customerId]';
import CustomersQueue from './pages/customers/queue';
import SupportIndex from './pages/support';
import SupportClassicSso from './pages/support/classic-sso';
import SupportClassicSsoShowConfig from './pages/support/classic-sso/ShowConfig';
import SupportNgLink from './pages/support/nglink';
import SupportNgLinkUpdatedWebpunch from './pages/support/nglink/UpdatedWebpunch';
import ModulesIndex from './pages/customers/modules';

const ERROR_CODES_TO_USE_ERROR_BOUNDARY = [401, 403];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
      useErrorBoundary: (error: any) =>
        ERROR_CODES_TO_USE_ERROR_BOUNDARY.includes(error?.response?.status),
    },
    mutations: {
      useErrorBoundary: (error: any) =>
        ERROR_CODES_TO_USE_ERROR_BOUNDARY.includes(error?.response?.status),
    },
  },
});

const AdminRoutes = () => {
  const { systemAdmin } = useUser();
  const { isCustomerManager, isLoading: isCustomerManagerLoading } =
    useIsCustomerManager();
  const canManageCustomerManagerTokens =
    systemAdmin || isCustomerManager || isCustomerManagerLoading;

  return (
    <Routes>
      <Route path="/" element={<Home />} />

      {canManageCustomerManagerTokens && (
        <>
          <Route path="/admins" element={<AdminsIndex />} />
          <Route
            path="/admins/customer-manager-token"
            element={<AdminsCustomerManagerToken />}
          />
        </>
      )}

      {systemAdmin && (
        <>
          {/* Admin routes */}
          <Route path="/admins/create" element={<AdminsCreate />} />
          <Route path="/admins/delete" element={<AdminsDelete />} />
          <Route
            path="/admins/system-admin-token"
            element={<AdminsSystemAdminToken />}
          />

          {/* Customer routes */}
          <Route path="/customers/queue" element={<CustomersQueue />} />
          <Route
            path="/customers/create/in-progress"
            element={<CustomersCreateInProgress />}
          />
          <Route path="/customers/modules" element={<ModulesIndex />} />

          {/* Support routes */}
          <Route path="/support" element={<SupportIndex />} />
          <Route path="/support/classic-sso" element={<SupportClassicSso />} />
          <Route
            path="/support/classic-sso/ShowConfig"
            element={<SupportClassicSsoShowConfig />}
          />
          <Route path="/support/nglink" element={<SupportNgLink />} />
          <Route
            path="/support/nglink/UpdatedWebpunch"
            element={<SupportNgLinkUpdatedWebpunch />}
          />
        </>
      )}

      {/* Customer routes */}
      <Route path="/customers" element={<CustomersIndex />} />
      <Route path="/customers/create" element={<CustomersCreate />} />
      <Route
        path="/customers/create/in-progress/:progressId"
        element={<CustomersCreateInProgressById />}
      />
      <Route path="/customers/:customerId" element={<CustomersById />} />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App = () => {
  const { t } = useTranslate();

  ConfigProvider.config({
    theme: {
      primaryColor: '#2d7b88',
      errorColor: '#b22f2f',
      warningColor: '#f0bb00',
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ConfigProvider
          form={{
            validateMessages: {
              required: t('form.required'),
            },
          }}>
          <ThemeProviderWithPersistence>
            <div className={styles.container}>
              <Navbar />
              <main className={styles.main}>
                <AdminRoutes />
              </main>
            </div>
          </ThemeProviderWithPersistence>
        </ConfigProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
